import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { api, apiJson, getAccessToken } from '../lib/api.js';
import { findCountry, formatPhone } from '../lib/countries.js';
import { trackEvent } from '../lib/analytics.js';
import UserActionsModal from '../components/UserActionsModal.jsx';
import AnalyticsDashboard from '../components/AnalyticsDashboard.jsx';

const PAGE = { background: '#f6f4fb', minHeight: '100vh', padding: 28, color: '#1f1240' };
const CARD = {
  background: '#fff',
  borderRadius: 16,
  padding: '18px 22px',
  border: '1px solid #ece8f6',
  boxShadow: '0 8px 22px rgba(20, 5, 60, 0.05)',
};
const SECTION_HEAD = { fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#5b21b6', marginBottom: 8 };
const PRIMARY_BTN = { padding: '8px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #5b21b6, #7c3aed)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13 };
const SECONDARY_BTN = { padding: '8px 14px', borderRadius: 8, border: '1px solid #d8d6e6', background: '#fff', color: '#3f3a5e', fontWeight: 600, cursor: 'pointer', fontSize: 13 };

function Flag({ code }) {
  const c = findCountry(code);
  if (!c) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <span title={`${c.name} · ${c.dial}`} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
      <span style={{ fontSize: 16 }}>{c.flag}</span>
      <span style={{ fontSize: 11, color: '#4b4361' }}>{c.code}</span>
      <span style={{ fontSize: 11, color: '#6b7280' }}>{c.dial}</span>
    </span>
  );
}

function PhoneCell({ phone, country }) {
  if (!phone) return <span style={{ color: '#9ca3af' }}>—</span>;
  const c = findCountry(country);
  const formatted = formatPhone(phone, country);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontVariantNumeric: 'tabular-nums' }}>
      <span style={{ color: '#1f1240', fontWeight: 500 }}>
        {c ? <span style={{ marginRight: 6 }}>{c.flag}</span> : null}
        {formatted}
      </span>
      {c && (
        <span style={{ fontSize: 11, color: '#6b7280' }}>{c.name}</span>
      )}
    </div>
  );
}

const RolePill = ({ role }) => {
  const colors = {
    student: ['#dbeafe', '#1e40af'],
    parent: ['#fef3c7', '#92400e'],
    teacher: ['#fce7f3', '#9d174d'],
    admin: ['#dcfce7', '#166534'],
    superadmin: ['#f3e8ff', '#6b21a8'],
  }[role] || ['#e5e7eb', '#374151'];
  return (
    <span
      style={{
        background: colors[0],
        color: colors[1],
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'capitalize',
      }}
    >
      {role}
    </span>
  );
};

function ContactCell({ contacts }) {
  if (!contacts?.length) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {contacts.map((c) => (
        <div key={c.id || c.email} style={{ fontSize: 12 }}>
          <strong style={{ color: '#1f1240' }}>{c.name}</strong>{' '}
          <span style={{ color: '#4b4361' }}>· {c.email}</span>
          {c.phone && (
            <div style={{ color: '#4b4361', fontVariantNumeric: 'tabular-nums' }}>
              {findCountry(c.phone_country)?.flag} {formatPhone(c.phone, c.phone_country)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function StatCard({ label, value, accent = '#5b21b6', active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        ...CARD,
        borderLeft: `4px solid ${accent}`, padding: '14px 16px',
        cursor: onClick ? 'pointer' : 'default',
        background: active ? '#faf7ff' : '#fff',
        boxShadow: active ? `0 0 0 2px ${accent}` : CARD.boxShadow,
        transition: 'box-shadow 0.15s',
      }}
    >
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: active ? accent : '#1f1240', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function IntegrationCard({ item, onTest }) {
  return (
    <div style={{ ...CARD, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <strong style={{ fontSize: 14.5, color: '#1f1240' }}>{item.label}</strong>
        <span
          style={{
            background: item.configured ? '#dcfce7' : '#fee2e2',
            color: item.configured ? '#166534' : '#991b1b',
            padding: '2px 10px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {item.configured ? '✓ Connected' : '✗ Not set'}
        </span>
      </div>
      {item.from && (
        <div style={{ fontSize: 12, color: '#4b4361' }}>
          <span style={{ color: '#6b7280' }}>Using:</span>{' '}
          <code style={{ background: '#f3eefc', padding: '1px 6px', borderRadius: 4, color: '#5b21b6' }}>{item.from}</code>
        </div>
      )}
      <div style={{ fontSize: 12, color: '#6b7280' }}>
        <span style={{ fontWeight: 600 }}>Env vars:</span> {item.env.join(', ')}
      </div>
      {item.docs && (
        <div style={{ fontSize: 11.5, color: '#6b7280', fontStyle: 'italic' }}>{item.docs}</div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {item.key === 'gmail' && onTest && (
          <button type="button" style={{ ...SECONDARY_BTN }} onClick={onTest}>
            Send test email →
          </button>
        )}
        {item.link && (
          <a href={item.link} target="_blank" rel="noreferrer" style={{ ...SECONDARY_BTN, textDecoration: 'none', display: 'inline-block' }}>
            {item.linkLabel || 'Open →'}
          </a>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('analytics');
  const [data, setData] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [actionUser, setActionUser] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(null); // 'email' | 'mailchimp' | null
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkBody, setBulkBody] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const fetchUsers = useCallback(async () => {
    setErr('');
    try {
      const qs = new URLSearchParams();
      if (search) qs.set('search', search);
      if (roleFilter) qs.set('role', roleFilter);
      const d = await apiJson(`/api/users/analytics/overview?${qs}`);
      setData(d);
    } catch (e) {
      setErr(e.message);
    }
  }, [search, roleFilter]);

  const fetchIntegrations = useCallback(async () => {
    try {
      const d = await apiJson('/api/admin/integrations');
      // Patch GA card with client-side measurement ID if the server didn't see it
      const gaClient = import.meta.env.VITE_GA_MEASUREMENT_ID;
      const patched = d.integrations.map((i) => {
        if (i.key === 'analytics' && gaClient && !i.configured) {
          return { ...i, configured: true, from: gaClient, link: `https://analytics.google.com/analytics/web/`, linkLabel: 'Open Google Analytics →' };
        }
        return i;
      });
      setIntegrations(patched);
    } catch (e) {
      console.warn(e);
    }
  }, []);

  const fetchFeedback = useCallback(async () => {
    try {
      const d = await apiJson('/api/feedback');
      setFeedback(d.feedback);
    } catch (e) {
      console.warn(e);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (user.role !== 'admin' && user.role !== 'superadmin') { navigate('/', { replace: true }); return; }
    fetchUsers();
    fetchIntegrations();
    fetchFeedback();
  }, [user, loading, navigate, fetchUsers, fetchIntegrations, fetchFeedback]);

  const allSelectedIds = useMemo(() => new Set((data?.users || []).map((u) => u.id)), [data]);
  const toggleAll = () => {
    if (selected.size === allSelectedIds.size && allSelectedIds.size > 0) {
      setSelected(new Set());
    } else {
      setSelected(allSelectedIds);
    }
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const downloadCsv = async () => {
    const token = getAccessToken();
    const qs = new URLSearchParams();
    if (roleFilter) qs.set('role', roleFilter);
    if (search) qs.set('search', search);
    const res = await fetch(`/api/users/export/csv?${qs}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) { setErr('Export failed'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `medic-mind-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runBulkMailchimp = async () => {
    setBulkBusy(true);
    setBulkStatus('');
    try {
      const r = await apiJson('/api/users/bulk/mailchimp-sync', {
        method: 'POST',
        body: { userIds: Array.from(selected) },
      });
      const ok = r.results.filter((x) => x.ok).length;
      setBulkStatus(`Synced ${ok} of ${r.results.length} to Mailchimp.`);
      trackEvent('admin_bulk_mailchimp_sync', { count: r.results.length, ok });
    } catch (e) {
      setBulkStatus(`Failed: ${e.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkEmail = async () => {
    if (!bulkSubject.trim() || !bulkBody.trim()) return;
    setBulkBusy(true);
    setBulkStatus('');
    try {
      const r = await apiJson('/api/users/bulk/email', {
        method: 'POST',
        body: { userIds: Array.from(selected), subject: bulkSubject, body: bulkBody },
      });
      setBulkStatus(r.stubbed ? `Stubbed ${r.sent} emails to server console (Gmail OAuth not configured).` : `Sent ${r.sent} of ${r.count}.`);
      trackEvent('admin_bulk_email', { count: r.count, sent: r.sent });
      setBulkOpen(null);
      setBulkSubject('');
      setBulkBody('');
    } catch (e) {
      setBulkStatus(`Failed: ${e.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const testEmail = async () => {
    try {
      const r = await apiJson('/api/admin/test-email', { method: 'POST' });
      alert(r.stubbed ? 'Test email stubbed to server console (Gmail OAuth env vars not set yet).' : r.ok ? 'Test email sent ✓' : `Failed: ${r.error}`);
    } catch (e) {
      alert(`Failed: ${e.message}`);
    }
  };

  if (loading) return <div style={PAGE}>Loading…</div>;

  const users = data?.users || [];
  const totals = data?.totals || {};

  let displayUsers = [...(users || [])];
  if (verifiedFilter === 'verified') displayUsers = displayUsers.filter((u) => u.email_verified);
  if (verifiedFilter === 'unverified') displayUsers = displayUsers.filter((u) => !u.email_verified);
  if (sortBy) {
    displayUsers.sort((a, b) => {
      const av = sortBy === 'name' ? (a.name || '') : sortBy === 'email' ? (a.email || '') : sortBy === 'joined' ? a.created_at : '';
      const bv = sortBy === 'name' ? (b.name || '') : sortBy === 'email' ? (b.email || '') : sortBy === 'joined' ? b.created_at : '';
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  };
  const sortArrow = (col) => sortBy === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <div style={PAGE}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={SECTION_HEAD}>Admin</div>
          <h1 style={{ margin: '4px 0 0', fontSize: 26, color: '#1f1240', fontWeight: 800 }}>Medic Mind control panel</h1>
          <p style={{ margin: '4px 0 0', color: '#4b4361', fontSize: 14 }}>
            Manage users, send marketing emails, sync to Mailchimp, export data and monitor integrations.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={SECONDARY_BTN} onClick={() => navigate('/')}>← Back to site</button>
          <button type="button" style={PRIMARY_BTN} onClick={() => setCreateOpen(true)}>+ Create user</button>
          <button type="button" style={SECONDARY_BTN} onClick={downloadCsv}>⬇ Export CSV</button>
          <button type="button" style={SECONDARY_BTN} onClick={() => { fetchUsers(); fetchIntegrations(); fetchFeedback(); }}>↻ Refresh</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #ece8f6', marginBottom: 18 }}>
        {[
          { id: 'analytics', label: 'Analytics' },
          { id: 'users', label: 'Users' },
          { id: 'feedback', label: `Feedback${feedback?.length ? ` (${feedback.length})` : ''}` },
          { id: 'integrations', label: 'Integrations' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === t.id ? '2px solid #5b21b6' : '2px solid transparent',
              color: tab === t.id ? '#5b21b6' : '#4b4361',
              fontWeight: tab === t.id ? 700 : 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'analytics' && <AnalyticsDashboard />}

      {tab === 'users' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
            <StatCard label="Total" value={totals.total} active={!roleFilter} onClick={() => { setRoleFilter(''); fetchUsers(); }} />
            <StatCard label="Students" value={totals.students} accent="#1e40af" active={roleFilter === 'student'} onClick={() => { setRoleFilter('student'); fetchUsers(); }} />
            <StatCard label="Parents" value={totals.parents} accent="#92400e" active={roleFilter === 'parent'} onClick={() => { setRoleFilter('parent'); fetchUsers(); }} />
            <StatCard label="Teachers" value={totals.teachers || 0} accent="#9d174d" active={roleFilter === 'teacher'} onClick={() => { setRoleFilter('teacher'); fetchUsers(); }} />
            <StatCard label="Admins" value={totals.admins + totals.superadmins} accent="#166534" active={roleFilter === 'admin'} onClick={() => { setRoleFilter('admin'); fetchUsers(); }} />
            <StatCard label="Verified" value={totals.verified} accent="#7c3aed" active={verifiedFilter === 'verified'} onClick={() => setVerifiedFilter((v) => v === 'verified' ? '' : 'verified')} />
            <StatCard label="New (7d)" value={totals.new_7d} accent="#0891b2" />
            <StatCard label="Mkt opt-in" value={totals.marketing_opt_in} accent="#db2777" />
          </div>

          <div style={{ ...CARD, marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
              <input
                type="text"
                placeholder="Search by name, email, or phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                style={{ flex: '1 1 280px', padding: '10px 12px', borderRadius: 10, border: '1px solid #d8d6e6', fontSize: 14, color: '#1f1240', background: '#fff' }}
              />
              <button type="button" style={PRIMARY_BTN} onClick={fetchUsers}>Search</button>
              {(search || roleFilter || verifiedFilter) && (
                <button type="button" style={SECONDARY_BTN} onClick={() => { setSearch(''); setRoleFilter(''); setVerifiedFilter(''); }}>✕ Clear</button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 2 }}>Role</span>
              {[
                { v: '', label: 'All', count: totals.total },
                { v: 'student', label: 'Students', count: totals.students },
                { v: 'parent', label: 'Parents', count: totals.parents },
                { v: 'teacher', label: 'Teachers', count: totals.teachers || 0 },
                { v: 'admin', label: 'Admins', count: totals.admins + totals.superadmins },
              ].map((p) => (
                <button key={p.v} type="button" onClick={() => { setRoleFilter(p.v); fetchUsers(); }} style={{
                  padding: '5px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${roleFilter === p.v ? '#5b21b6' : '#d8d6e6'}`,
                  background: roleFilter === p.v ? '#5b21b6' : '#fff',
                  color: roleFilter === p.v ? '#fff' : '#4b4361',
                }}>
                  {p.label} <span style={{ opacity: 0.75, fontSize: 11 }}>({p.count})</span>
                </button>
              ))}
              <span style={{ color: '#d8d6e6', margin: '0 4px' }}>|</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 2 }}>Verified</span>
              {[{ v: '', label: 'All' }, { v: 'verified', label: '✓ Yes' }, { v: 'unverified', label: '✗ No' }].map((p) => (
                <button key={p.v} type="button" onClick={() => setVerifiedFilter(p.v)} style={{
                  padding: '5px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${verifiedFilter === p.v ? '#7c3aed' : '#d8d6e6'}`,
                  background: verifiedFilter === p.v ? '#7c3aed' : '#fff',
                  color: verifiedFilter === p.v ? '#fff' : '#4b4361',
                }}>{p.label}</button>
              ))}
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                {displayUsers.length} user{displayUsers.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {selected.size > 0 && (
            <div style={{ ...CARD, marginBottom: 14, background: '#f3eefc', borderColor: '#7c3aed', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <strong style={{ color: '#5b21b6' }}>{selected.size} selected</strong>
              <button type="button" style={PRIMARY_BTN} onClick={() => setBulkOpen('email')}>📧 Email selected</button>
              <button type="button" style={PRIMARY_BTN} onClick={runBulkMailchimp} disabled={bulkBusy}>🐵 Sync to Mailchimp</button>
              <button type="button" style={SECONDARY_BTN} onClick={() => setSelected(new Set())}>Clear</button>
              {bulkStatus && <span style={{ fontSize: 13, color: '#1f1240' }}>{bulkStatus}</span>}
            </div>
          )}

          {err && <div className="mm-auth-error" style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8, marginBottom: 12 }}>{err}</div>}

          <div style={{ ...CARD, padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: '#1f1240' }}>
              <thead>
                <tr style={{ background: '#faf8ff', textAlign: 'left' }}>
                  <th style={{ padding: '12px 14px', width: 36 }}>
                    <input
                      type="checkbox"
                      checked={selected.size > 0 && selected.size === allSelectedIds.size}
                      onChange={toggleAll}
                    />
                  </th>
                  {[
                    { label: 'User', col: 'name' },
                    { label: 'Role', col: '' },
                    { label: 'Phone', col: '' },
                    { label: 'Counterpart', col: '' },
                    { label: 'Verified', col: '' },
                    { label: 'Last login', col: '' },
                    { label: 'Joined', col: 'joined' },
                    { label: '', col: '' },
                  ].map((h) => (
                    <th key={h.label} onClick={h.col ? () => toggleSort(h.col) : undefined}
                      style={{ padding: '12px 14px', fontSize: 11.5, fontWeight: 700, color: '#5b21b6', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #ece8f6', whiteSpace: 'nowrap', cursor: h.col ? 'pointer' : 'default', userSelect: 'none' }}>
                      {h.label}{sortArrow(h.col)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayUsers.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>No users match those filters.</td></tr>
                )}
                {displayUsers.map((u) => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #f1eef9', background: selected.has(u.id) ? '#faf8ff' : '#fff' }}>
                    <td style={{ padding: '10px 14px' }}>
                      <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleOne(u.id)} />
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ fontWeight: 600, color: '#1f1240' }}>{u.name || '—'}</div>
                      <div style={{ color: '#4b4361', fontSize: 12 }}>{u.email}</div>
                      {u.signup_ip && <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{u.signup_ip}</div>}
                    </td>
                    <td style={{ padding: '10px 14px' }}><RolePill role={u.role} /></td>
                    <td style={{ padding: '10px 14px' }}><PhoneCell phone={u.phone} country={u.phone_country} /></td>
                    <td style={{ padding: '10px 14px' }}><ContactCell contacts={u.contacts} /></td>
                    <td style={{ padding: '10px 14px' }}>
                      {u.email_verified
                        ? <span style={{ color: '#15803d', fontWeight: 700 }}>✓</span>
                        : <span style={{ color: '#dc2626', fontWeight: 700 }}>✗</span>}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#4b4361' }}>
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}
                      {u.last_login_ip && <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#6b7280' }}>{u.last_login_ip}</div>}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#4b4361' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <button type="button" onClick={() => setActionUser(u)} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #5b21b6', background: '#fff', color: '#5b21b6', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'feedback' && (
        <FeedbackTab feedback={feedback} onResolve={async (id) => {
          try { await apiJson(`/api/feedback/${id}/resolve`, { method: 'POST' }); fetchFeedback(); }
          catch (e) { alert(e.message); }
        }} />
      )}

      {tab === 'integrations' && (
        <>
          <p style={{ color: '#4b4361', fontSize: 14, marginTop: 0 }}>
            Status of every external integration. Anything showing "Not set" is configured
            via Railway env vars OR the panels below.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 22 }}>
            {(integrations || []).map((i) => (
              <IntegrationCard key={i.key} item={i} onTest={testEmail} />
            ))}
          </div>
          <GmailSetup onSaved={fetchIntegrations} />
        </>
      )}

      {actionUser && (
        <UserActionsModal targetUser={actionUser} onClose={() => setActionUser(null)} onDidUpdate={fetchUsers} />
      )}

      {createOpen && (
        <CreateUserModal
          currentUserRole={user.role}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); fetchUsers(); }}
        />
      )}

      {bulkOpen === 'email' && (
        <div onClick={() => !bulkBusy && setBulkOpen(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(8,4,22,0.78)', backdropFilter: 'blur(6px)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="mm-auth-card" onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: '28px 30px', width: '100%', maxWidth: 540 }}>
            <div style={SECTION_HEAD}>Bulk email · {selected.size} recipients</div>
            <h2 style={{ margin: '4px 0 14px', fontSize: 20, color: '#1f1240' }}>Send the same email to everyone you selected</h2>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#3f3a5e', display: 'block', marginBottom: 6 }}>Subject</label>
            <input type="text" value={bulkSubject} onChange={(e) => setBulkSubject(e.target.value)} placeholder="A short, clear subject" style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #d8d6e6', fontSize: 14, color: '#1f1240', marginBottom: 12, boxSizing: 'border-box' }} />
            <label style={{ fontSize: 13, fontWeight: 600, color: '#3f3a5e', display: 'block', marginBottom: 6 }}>Message</label>
            <textarea value={bulkBody} onChange={(e) => setBulkBody(e.target.value)} placeholder="Hi {{name will be added}}, …" style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #d8d6e6', fontSize: 14, minHeight: 160, color: '#1f1240', marginBottom: 14, boxSizing: 'border-box', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" style={SECONDARY_BTN} onClick={() => setBulkOpen(null)} disabled={bulkBusy}>Cancel</button>
              <button type="button" style={PRIMARY_BTN} onClick={runBulkEmail} disabled={bulkBusy || !bulkSubject.trim() || !bulkBody.trim()}>{bulkBusy ? 'Sending…' : `Send ${selected.size}`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FeedbackTab({ feedback, onResolve }) {
  if (!feedback) return <div style={{ color: '#4b4361' }}>Loading…</div>;
  if (feedback.length === 0) {
    return <div style={{ ...CARD, color: '#4b4361', textAlign: 'center', padding: 28 }}>No feedback yet. The 🐛/💡 widget on the bottom-right of the app posts here.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {feedback.map((f) => (
        <div key={f.id} style={{ ...CARD, padding: '14px 18px', opacity: f.resolved_at ? 0.6 : 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#5b21b6', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {f.type}{f.path ? ` · ${f.path}` : ''}
              </div>
              <p style={{ margin: '6px 0 6px', whiteSpace: 'pre-wrap', color: '#1f1240', fontSize: 14, lineHeight: 1.5 }}>{f.message}</p>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {f.user_email || f.email || 'anonymous'} · {new Date(f.created_at).toLocaleString()}
                {f.ip && ` · ${f.ip}`}
              </div>
            </div>
            {!f.resolved_at && (
              <button type="button" style={SECONDARY_BTN} onClick={() => onResolve(f.id)}>Mark resolved</button>
            )}
            {f.resolved_at && <span style={{ fontSize: 12, color: '#166534' }}>✓ Resolved {new Date(f.resolved_at).toLocaleDateString()}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function CreateUserModal({ currentUserRole, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('student');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const roles = ['student', 'parent', 'teacher', 'admin'];
  if (currentUserRole === 'superadmin') roles.push('superadmin');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      await apiJson('/api/users', {
        method: 'POST',
        body: { name, email, password, role, marketingConsent },
      });
      onCreated();
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(8,4,22,0.78)', backdropFilter: 'blur(6px)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="mm-auth-card" onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: '28px 30px', width: '100%', maxWidth: 480 }}>
        <div style={SECTION_HEAD}>Create user</div>
        <h2 style={{ margin: '4px 0 14px', fontSize: 20, color: '#1f1240' }}>Add a new account</h2>
        <p style={{ fontSize: 13, color: '#4b4361', margin: '0 0 16px' }}>
          The user will be created with their email already marked verified.
          Share the password with them through a safe channel.
        </p>

        {err && <div className="mm-auth-error" style={{ background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{err}</div>}

        <form onSubmit={submit}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#3f3a5e', display: 'block', marginBottom: 4 }}>Full name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #d8d6e6', marginBottom: 10, fontSize: 14, color: '#1f1240', boxSizing: 'border-box' }} />

          <label style={{ fontSize: 13, fontWeight: 600, color: '#3f3a5e', display: 'block', marginBottom: 4 }}>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #d8d6e6', marginBottom: 10, fontSize: 14, color: '#1f1240', boxSizing: 'border-box' }} />

          <label style={{ fontSize: 13, fontWeight: 600, color: '#3f3a5e', display: 'block', marginBottom: 4 }}>Initial password (8+ chars, 1 letter + 1 number)</label>
          <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #d8d6e6', marginBottom: 10, fontSize: 14, color: '#1f1240', boxSizing: 'border-box' }} placeholder="e.g. TempPass2026" />

          <label style={{ fontSize: 13, fontWeight: 600, color: '#3f3a5e', display: 'block', marginBottom: 4 }}>Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #d8d6e6', marginBottom: 14, fontSize: 14, color: '#1f1240', background: '#fff', boxSizing: 'border-box', textTransform: 'capitalize' }}>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#3f3a5e', marginBottom: 16 }}>
            <input type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)} />
            Marketing consent (push to Mailchimp on save)
          </label>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" style={SECONDARY_BTN} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" style={PRIMARY_BTN} disabled={submitting}>{submitting ? 'Creating…' : 'Create user'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GmailSetup({ onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ gmail_user: '', client_id: '', client_secret: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await apiJson('/api/admin/settings/gmail');
      setCfg(d);
      setForm((f) => ({ ...f, gmail_user: d.gmail_user || '' }));
    } catch (e) { setMsg(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      await apiJson('/api/admin/settings/gmail', {
        method: 'POST',
        body: form,
      });
      setMsg('Saved. Now click Authorise to grant Gmail access.');
      await load();
      onSaved?.();
    } catch (e2) {
      setMsg(`Failed: ${e2.message}`);
    } finally {
      setSaving(false);
    }
  };

  const authorise = async () => {
    setMsg('');
    try {
      const { url } = await apiJson('/api/admin/oauth/google/start');
      window.open(url, '_blank', 'width=560,height=720');
      setMsg('Authorisation window opened. After granting access, refresh this page.');
    } catch (e) { setMsg(`Failed: ${e.message}`); }
  };

  if (!cfg) return <div style={CARD}>Loading Gmail setup…</div>;

  return (
    <div style={{ ...CARD, padding: '20px 22px' }}>
      <div style={SECTION_HEAD}>Gmail setup</div>
      <h2 style={{ margin: '4px 0 6px', fontSize: 18, color: '#1f1240' }}>Connect Medic Mind's sending mailbox</h2>
      <p style={{ color: '#4b4361', fontSize: 13.5, margin: '0 0 12px' }}>
        Set the OAuth Client ID + Secret, then click <strong>Authorise</strong> to grant
        access. The refresh token is captured server-side and stored in the DB. No
        redeploy needed when you rotate it.
      </p>
      <p style={{ color: '#6b7280', fontSize: 12.5, margin: '0 0 14px' }}>
        <strong>Required redirect URI</strong> (paste into Google Cloud Console OAuth Client):{' '}
        <code style={{ background: '#f3eefc', padding: '1px 6px', borderRadius: 4, color: '#5b21b6' }}>{cfg.expected_redirect_uri}</code>
      </p>

      <form onSubmit={save} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#3f3a5e', display: 'block', marginBottom: 4 }}>Mailbox address (GMAIL_USER)</label>
          <input
            type="email"
            value={form.gmail_user}
            onChange={(e) => setForm({ ...form, gmail_user: e.target.value })}
            placeholder="info@medicmind.co.uk"
            required
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d8d6e6', fontSize: 13.5, color: '#1f1240', boxSizing: 'border-box' }}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#3f3a5e', display: 'block', marginBottom: 4 }}>Client ID</label>
          <input
            type="text"
            value={form.client_id}
            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            placeholder={cfg.client_id || 'paste from Google Cloud → Credentials'}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d8d6e6', fontSize: 13.5, color: '#1f1240', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#3f3a5e', display: 'block', marginBottom: 4 }}>Client Secret</label>
          <input
            type="password"
            value={form.client_secret}
            onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
            placeholder={cfg.client_secret_set ? '(saved — leave blank to keep)' : 'GOCSPX-…'}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d8d6e6', fontSize: 13.5, color: '#1f1240', boxSizing: 'border-box', fontFamily: 'monospace' }}
          />
        </div>
        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="submit" style={PRIMARY_BTN} disabled={saving}>{saving ? 'Saving…' : 'Save credentials'}</button>
          <button type="button" style={SECONDARY_BTN} onClick={authorise} disabled={!cfg.client_secret_set && !form.client_secret}>
            Authorise Gmail →
          </button>
          <span style={{ fontSize: 12, color: cfg.refresh_token_set ? '#166534' : '#991b1b', fontWeight: 600 }}>
            {cfg.refresh_token_set ? '✓ Refresh token saved' : '✗ No refresh token yet'}
          </span>
          {msg && <span style={{ fontSize: 12, color: '#4b4361' }}>{msg}</span>}
        </div>
      </form>
    </div>
  );
}
